import {
  type FilterGroup,
  type FilterInput,
  LogicalOperator,
  NO_FILTER_OPERATOR,
  OperatorOption,
} from '@/components/publicSearch/constants';
import { hasWildcardChars } from '@/components/publicSearch/search/animalIdentChipFilter';
import type { GridSortModel } from '@mui/x-data-grid-pro';
import type { SortOrderEnum } from '@/lib/types';

export const PublicSearchUrlOperator = {
  between: 'between',
  contains: 'contains',
  endsWith: 'endsWith',
  greaterThan: 'gt',
  greaterThanOrEqual: 'gte',
  lessThan: 'lt',
  lessThanOrEqual: 'lte',
  lookup: 'lookup',
  noOperator: 'none',
  notEmpty: 'notEmpty',
  notEquals: 'neq',
  startsWith: 'startsWith',
} as const;

export const PublicSearchUrlLogicalOperator = {
  or: 'or',
} as const;

const LOOKUP_OPERATOR = 'LOOKUP';
const EMPTY_VALUE = '';

export function deserializePublicSearchString(value: unknown): string {
  return typeof value === typeof EMPTY_VALUE ? (value as string) : EMPTY_VALUE;
}

const OP_TO_URL: Record<string, string> = {
  [OperatorOption.contains.value]: PublicSearchUrlOperator.contains,
  [OperatorOption.between.value]: PublicSearchUrlOperator.between,
  [OperatorOption.endsWith.value]: PublicSearchUrlOperator.endsWith,
  [OperatorOption.greaterThan.value]: PublicSearchUrlOperator.greaterThan,
  [OperatorOption.greaterThanOrEqual.value]:
    PublicSearchUrlOperator.greaterThanOrEqual,
  [OperatorOption.isNotEmpty.value]: PublicSearchUrlOperator.notEmpty,
  [OperatorOption.lessThan.value]: PublicSearchUrlOperator.lessThan,
  [OperatorOption.lessThanOrEqual.value]:
    PublicSearchUrlOperator.lessThanOrEqual,
  [OperatorOption.notEquals.value]: PublicSearchUrlOperator.notEquals,
  [OperatorOption.startsWith.value]: PublicSearchUrlOperator.startsWith,
  [LOOKUP_OPERATOR]: PublicSearchUrlOperator.lookup,
};

const URL_TO_OP: Record<string, string> = Object.fromEntries(
  Object.entries(OP_TO_URL).map(([gql, url]) => [url, gql]),
);

export function serializePublicSearchOperator(
  operator: string,
): string | undefined {
  if (!operator) return PublicSearchUrlOperator.noOperator;
  if (operator === OperatorOption.equals.value) return undefined;
  return OP_TO_URL[operator] ?? operator;
}

export function deserializePublicSearchOperator(
  operator: string | undefined,
  value = EMPTY_VALUE,
): string {
  if (operator === PublicSearchUrlOperator.noOperator)
    return NO_FILTER_OPERATOR;
  if (!operator) {
    return hasWildcardChars(value)
      ? NO_FILTER_OPERATOR
      : OperatorOption.equals.value;
  }
  return URL_TO_OP[operator] ?? operator;
}

export type PublicSearchUrlFilter = {
  field: string;
  op?: string;
  value?: string;
};

export type PublicSearchUrlFilterGroup = {
  filters: PublicSearchUrlFilter[];
  op?: typeof PublicSearchUrlLogicalOperator.or;
};

export type PublicSearchUrlSort = {
  field: string;
  dir: SortOrderEnum;
};

export type SerializedPublicSearchState = {
  q?: string;
  ai?: string;
  aiMode?: true;
  filters?: PublicSearchUrlFilter[];
  groups?: PublicSearchUrlFilterGroup[];
  sort?: PublicSearchUrlSort[];
  my?: true;
  onSale?: true;
  op?: typeof PublicSearchUrlLogicalOperator.or;
  gop?: typeof PublicSearchUrlLogicalOperator.or;
};

export type DeserializedPublicSearchFilterState = {
  filters: FilterInput[];
  filterGroups: FilterGroup[];
  commonFilterOperator: LogicalOperator;
  groupsOperator: LogicalOperator;
  gridSortModel: GridSortModel;
};

export function serializePublicSearchFilter(
  filter: FilterInput,
): PublicSearchUrlFilter {
  const serialized: PublicSearchUrlFilter = { field: filter.field };
  const operator = serializePublicSearchOperator(filter.operator);
  if (operator) serialized.op = operator;
  if (filter.value) serialized.value = filter.value;
  return serialized;
}

export function deserializePublicSearchFilter(
  filter: PublicSearchUrlFilter,
): FilterInput {
  return {
    field: deserializePublicSearchString(filter.field),
    operator: deserializePublicSearchOperator(filter.op, filter.value),
    value: deserializePublicSearchString(filter.value),
  };
}

export function serializePublicSearchFilterGroup(
  group: FilterGroup,
): PublicSearchUrlFilterGroup {
  return {
    filters: group.filters.map(serializePublicSearchFilter),
    ...(group.logicalOperator === LogicalOperator.or && {
      op: PublicSearchUrlLogicalOperator.or,
    }),
  };
}

export function deserializePublicSearchFilterGroup(
  group: PublicSearchUrlFilterGroup,
): FilterGroup {
  return {
    filters: (group.filters ?? []).map(deserializePublicSearchFilter),
    logicalOperator:
      group.op === PublicSearchUrlLogicalOperator.or
        ? LogicalOperator.or
        : LogicalOperator.and,
  };
}

export function serializePublicSearchSort(
  sortModel: GridSortModel,
): PublicSearchUrlSort[] {
  return sortModel
    .filter((sort) => sort.sort != null)
    .map((sort) => ({
      field: sort.field,
      dir: sort.sort as SortOrderEnum,
    }));
}

export function deserializePublicSearchSort(
  sortItems: PublicSearchUrlSort[] = [],
): GridSortModel {
  return sortItems.map((sort) => ({
    field: sort.field,
    sort: sort.dir ?? null,
  }));
}

export function deserializePublicSearchFilterState(
  state: SerializedPublicSearchState,
): DeserializedPublicSearchFilterState {
  return {
    filters: (state.filters ?? []).map(deserializePublicSearchFilter),
    filterGroups: (state.groups ?? []).map(deserializePublicSearchFilterGroup),
    commonFilterOperator:
      state.op === PublicSearchUrlLogicalOperator.or
        ? LogicalOperator.or
        : LogicalOperator.and,
    groupsOperator:
      state.gop === PublicSearchUrlLogicalOperator.or
        ? LogicalOperator.or
        : LogicalOperator.and,
    gridSortModel: deserializePublicSearchSort(state.sort),
  };
}
